import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useFormatMoneda } from '../utils/moneda';
import { useAuth } from '../context/AuthContext';

export default function Cobros() {
  const formatMoneda = useFormatMoneda();
  const { usuario } = useAuth();
  const puedeAnular = ['admin', 'supervisor'].includes(usuario?.rol);
  const [cobros, setCobros] = useState([]);
  const [anulandoId, setAnulandoId] = useState(null);
  const [modal, setModal] = useState(false);
  const [creditos, setCreditos] = useState([]);
  const [cuotas, setCuotas] = useState([]);
  const [form, setForm] = useState({ credito_id: '', cuota_id: '', monto: '', metodo: 'efectivo', observaciones: '' });
  const [filtros, setFiltros] = useState({ fecha_desde: '', fecha_hasta: '' });
  const [buscar, setBuscar] = useState('');
  const [expandido, setExpandido] = useState(new Set());
  const [loading, setLoading] = useState(false);

  function cargar() {
    api.get('/cobros', { params: filtros }).then(r => setCobros(r.data)).catch(() => toast.error('Error cargando cobros'));
  }

  useEffect(() => { cargar(); }, [filtros]);

  useEffect(() => {
    api.get('/creditos', { params: { estado: 'activo' } }).then(r => setCreditos(r.data)).catch(() => {});
  }, []);

  async function cargarCuotas(credito_id) {
    if (!credito_id) { setCuotas([]); return; }
    try {
      const res = await api.get(`/creditos/${credito_id}/cuotas`);
      setCuotas(res.data.filter(c => c.estado !== 'pagada'));
    } catch {
      setCuotas([]);
    }
  }

  async function registrar(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/cobros', { cuota_id: form.cuota_id, monto: parseFloat(form.monto), metodo: form.metodo, observaciones: form.observaciones });
      toast.success('Cobro registrado');
      setModal(false);
      setForm({ credito_id: '', cuota_id: '', monto: '', metodo: 'efectivo', observaciones: '' });
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al registrar cobro');
    } finally {
      setLoading(false);
    }
  }

  async function anularCobro(cobro) {
    if (!window.confirm(`¿Anular el cobro de ${formatMoneda(parseFloat(cobro.monto))} a ${cobro.cliente_nombre} (cuota #${cobro.cuota_numero})? Esto restará el monto de la cuota.`)) return;
    setAnulandoId(cobro.id);
    try {
      await api.delete(`/cobros/${cobro.id}`);
      toast.success('Cobro anulado');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al anular el cobro');
    } finally {
      setAnulandoId(null);
    }
  }

  function toggleExpandido(documento) {
    setExpandido(prev => {
      const next = new Set(prev);
      if (next.has(documento)) next.delete(documento); else next.add(documento);
      return next;
    });
  }

  const cobrosFiltrados = cobros.filter(c => {
    if (!buscar) return true;
    const q = buscar.toLowerCase();
    return c.cliente_nombre.toLowerCase().includes(q) || (c.cliente_documento || '').toLowerCase().includes(q);
  });

  // Agrupar por cliente para no repetir su nombre una vez por cada cuota pagada
  const grupos = [];
  const gruposPorDocumento = new Map();
  for (const c of cobrosFiltrados) {
    const key = c.cliente_documento || c.cliente_nombre;
    let grupo = gruposPorDocumento.get(key);
    if (!grupo) {
      grupo = { documento: key, nombre: c.cliente_nombre, pagos: [], total: 0 };
      gruposPorDocumento.set(key, grupo);
      grupos.push(grupo);
    }
    grupo.pagos.push(c);
    grupo.total += parseFloat(c.monto);
  }

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>Cobros</h2>
        <button style={styles.btnPrimary} onClick={() => setModal(true)}>+ Registrar Cobro</button>
      </div>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={styles.field}>
          <label style={styles.label}>Desde</label>
          <input type="date" value={filtros.fecha_desde} onChange={e => setFiltros({ ...filtros, fecha_desde: e.target.value })} style={styles.inputSm} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Hasta</label>
          <input type="date" value={filtros.fecha_hasta} onChange={e => setFiltros({ ...filtros, fecha_hasta: e.target.value })} style={styles.inputSm} />
        </div>
        <div style={{ ...styles.field, flex: 1, minWidth: 200 }}>
          <label style={styles.label}>Buscar cliente</label>
          <input
            placeholder="Nombre o documento..."
            value={buscar}
            onChange={e => setBuscar(e.target.value)}
            style={{ ...styles.inputSm, width: '100%', boxSizing: 'border-box' }}
          />
        </div>
      </div>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.thead}>
              <th>Cliente</th><th>Documento</th><th>Pagos</th><th>Total cobrado</th><th></th>
            </tr>
          </thead>
          <tbody>
            {grupos.map(g => {
              const abierto = expandido.has(g.documento);
              return (
                <React.Fragment key={g.documento}>
                  <tr style={{ ...styles.row, cursor: 'pointer' }} onClick={() => toggleExpandido(g.documento)}>
                    <td>{g.nombre}</td>
                    <td style={{ color: 'var(--text-3)' }}>{g.documento}</td>
                    <td>{g.pagos.length}</td>
                    <td style={{ color: '#34d399' }}>{formatMoneda(g.total)}</td>
                    <td style={{ color: '#38bdf8', fontSize: '0.85rem' }}>{abierto ? '▲ Ocultar' : '▼ Ver pagos'}</td>
                  </tr>
                  {abierto && (
                    <tr>
                      <td colSpan={5} style={{ padding: 0, background: 'var(--bg-app)' }}>
                        <table style={{ ...styles.table, margin: 0 }}>
                          <thead>
                            <tr style={{ ...styles.thead, fontSize: '0.75rem' }}>
                              <th style={{ paddingLeft: '2rem' }}>Fecha</th><th>Cuota #</th><th>Monto</th><th>Método</th><th>Cobrador</th>
                              {puedeAnular && <th></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {g.pagos.map(c => (
                              <tr key={c.id} style={styles.row}>
                                <td style={{ paddingLeft: '2rem' }}>{c.fecha}</td>
                                <td>#{c.cuota_numero}</td>
                                <td style={{ color: '#34d399' }}>{formatMoneda(parseFloat(c.monto))}</td>
                                <td>{c.metodo}</td>
                                <td>{c.cobrador_nombre}</td>
                                {puedeAnular && (
                                  <td>
                                    <button
                                      onClick={() => anularCobro(c)}
                                      disabled={anulandoId === c.id}
                                      style={styles.btnAnular}
                                    >
                                      {anulandoId === c.id ? 'Anulando...' : 'Anular'}
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {grupos.length === 0 && <p style={styles.empty}>No hay cobros en el período seleccionado</p>}
      </div>

      {modal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Registrar Cobro</h3>
            <form onSubmit={registrar}>
              <div style={styles.field}>
                <label style={styles.label}>Crédito</label>
                <select value={form.credito_id} onChange={e => { setForm({ ...form, credito_id: e.target.value, cuota_id: '' }); cargarCuotas(e.target.value); }} required style={styles.input}>
                  <option value="">Seleccionar crédito...</option>
                  {creditos.map(c => <option key={c.id} value={c.id}>{c.cliente_nombre} — {formatMoneda(parseFloat(c.monto))}</option>)}
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Cuota</label>
                <select value={form.cuota_id} onChange={e => setForm({ ...form, cuota_id: e.target.value })} required style={styles.input} disabled={!form.credito_id}>
                  <option value="">Seleccionar cuota...</option>
                  {cuotas.map(cu => <option key={cu.id} value={cu.id}>Cuota #{cu.numero} — Vence: {cu.fecha_vence} — Saldo: {formatMoneda(cu.monto - cu.monto_pagado)}</option>)}
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Monto a pagar</label>
                <input type="number" step="0.01" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} required style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Método de pago</label>
                <select value={form.metodo} onChange={e => setForm({ ...form, metodo: e.target.value })} style={styles.input}>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="nequi">Nequi</option>
                  <option value="daviplata">DaviPlata</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Observaciones</label>
                <input type="text" value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} style={styles.input} />
              </div>
              <div style={styles.modalActions}>
                <button type="button" style={styles.btnCancel} onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" style={styles.btnPrimary} disabled={loading}>{loading ? 'Registrando...' : 'Registrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  title: { margin: 0, color: 'var(--text-1)', fontSize: '1.5rem' },
  btnPrimary: { background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: '8px', padding: '0.6rem 1.2rem', fontWeight: 700, cursor: 'pointer' },
  btnCancel: { background: 'var(--bg-hover)', color: 'var(--text-1)', border: 'none', borderRadius: '8px', padding: '0.6rem 1.2rem', cursor: 'pointer' },
  btnAnular: { background: '#450a0a', color: '#fca5a5', border: 'none', borderRadius: '6px', padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 },
  tableWrap: { background: 'var(--bg-card)', borderRadius: '10px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--bg-app)', color: 'var(--text-2)', textAlign: 'left', fontSize: '0.8rem' },
  row: { borderBottom: '1px solid var(--border)', color: 'var(--text-1s)', fontSize: '0.9rem' },
  empty: { textAlign: 'center', color: 'var(--text-3)', padding: '2rem' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: 'var(--bg-card)', borderRadius: '12px', padding: '2rem', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { margin: '0 0 1.5rem', color: 'var(--text-1)' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', color: 'var(--text-2)', fontSize: '0.85rem', marginBottom: '0.3rem' },
  input: { width: '100%', padding: '0.65rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-1)', fontSize: '0.95rem', boxSizing: 'border-box' },
  inputSm: { padding: '0.5rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-1)', fontSize: '0.9rem' }
};
